import { type CalendarDate, getLocalTimeZone, parseDate, toCalendarDate, today } from '@internationalized/date';
import { DatePicker } from '@customerio/pluma-components/react';
import { useAuth } from '@/services/auth';
import { type AugmentedAccount, PlanFeature } from '@/services/auth/types';
import { isEmptyString } from '@/utils/string';
import type { ActivityLogFilters } from '../types';

/** The two date-range fields of the feed's filters. */
type DateRange = Pick<ActivityLogFilters, 'from' | 'to'>;

/** Activity time window is never wider than the last 30 days (inclusive of today). */
const MAX_DATE_RANGE_DAYS = 30;

interface DateRangeFilterProps {
  /** RFC3339 strings (or '') — the `from`/`to` query params forwarded to the logs endpoint. */
  from: string;
  to: string;
  onChange: (range: DateRange) => void;
}

/** `2026-06-30T…Z` → the `CalendarDate` the range picker wants; `''`/invalid → undefined. */
export function rfcToCalendarDate(value: string): CalendarDate | undefined {
  if (isEmptyString(value)) return undefined;
  try {
    return parseDate(value.slice(0, 10));
  } catch {
    return undefined;
  }
}

/**
 * Days back (inclusive of today) the picker allows: the account's `activity_log_days` plan gate,
 * capped at 30. The `/logs` endpoint drops events older than that gate, so a wider window would
 * only offer days that come back empty. Falls back to 30 when the gate is missing, disabled, or
 * has no positive limit.
 */
export function activityLogWindowDays(account: AugmentedAccount | null): number {
  const gate = account?.planFeatures[PlanFeature.ActivityLogDays];
  const limit = gate?.enabled === true ? gate.limit : false;
  const hasPlanDayLimit = limit !== false && limit > 0;
  return hasPlanDayLimit ? Math.min(limit, MAX_DATE_RANGE_DAYS) : MAX_DATE_RANGE_DAYS;
}

/** A `{ start, end }` calendar range → inclusive RFC3339 day bounds (start-of-day / end-of-day, UTC). */
export function rangeToFilters(range: { start: CalendarDate; end: CalendarDate }): DateRange {
  return { from: `${range.start.toString()}T00:00:00Z`, to: `${range.end.toString()}T23:59:59Z` };
}

/**
 * Date-range filter for the activity time window. Uses Pluma's range `DatePicker` (a single
 * control) with a `minDate` window sized by {@link activityLogWindowDays}.
 */
export function DateRangeFilter({ from, to, onChange }: DateRangeFilterProps) {
  const { account } = useAuth();

  const todayDate = today(getLocalTimeZone());
  const minDate = todayDate.subtract({ days: activityLogWindowDays(account) - 1 });

  const start = rfcToCalendarDate(from);
  const end = rfcToCalendarDate(to);
  const value = start != null && end != null ? { start, end } : undefined;

  return (
    <DatePicker
      label="Date range"
      isRange
      withPresets
      value={value}
      minDate={minDate}
      maxDate={todayDate}
      // Range mode passes a `{ start, end }` value; `'start' in next` narrows it (single-date /
      // zoned values, which this picker doesn't produce, and null → clear the filter).
      onChange={(next) =>
        onChange(
          next != null && 'start' in next && 'end' in next
            ? rangeToFilters({ start: toCalendarDate(next.start), end: toCalendarDate(next.end) })
            : { from: '', to: '' },
        )
      }
      size="sm"
    />
  );
}
